import React, {useContext, useMemo, useState} from 'react';
import {UserContext} from '../../../../src/contexts/UserContext';
import MessageReceiver, {
    makeOnDeleteAttachListener,
    makeOnDropAttachListener
} from '../../../../src/components/Chat/MessageReceiver/MessageReceiver';
import AttachmentsPreviews from '../../../../src/components/Chat/AttachmentsPreviews/AttachmentsPreviews';
import Translate from '../../../../src/components/Translate/Translate';
import classnames from '../../../../src/helpers/classnames';
import {BootstrapColors} from '../../../../src/@types/Bootstrap';
import {AttentionMessageProps} from './messages';
import app from '../../../../src/helpers/app';

type ControlProps = React.ComponentProps<typeof OrderAttentionControls>;

export function makeOrderAttentionControlListener<Key extends 'onAccept' | 'onReject'>(
        routeName: string,
        modelId: string,
        refetchModel?: () => Promise<any>,
        onFail?: (e: Error) => any,
        onSuccess?: (value: any) => any,
        validateRequestBody?: (message: string | null, attachments: File[]) => Promise<any> | unknown,
): ControlProps[Key] {
    return (message, attachments) => {
        if (validateRequestBody) {
            const res = validateRequestBody(message, attachments);
            if (res instanceof Promise) {
                return res;
            }
        }

        return app('http').route(routeName, {
            id: modelId
        }, {
            headers: {'content-type': 'multipart/form-data'},
            body: {['controller-name']: 'Orders', message, attachments},
        }).then(value => {
            if (refetchModel) {
                refetchModel().then();
            }

            return onSuccess ? onSuccess(value) : value;
        }).catch(onFail);
    };
}

export default function OrderAttentionControls(props: {
    model: AttentionMessageProps['model'],
    acceptPermissions?: string[],
    rejectPermissions?: string[],
    hideAccept?: boolean,
    hideReject?: boolean,
    withoutCommentField?: boolean,
    commentFieldPlaceholder?: string,
    onAccept?: (message: string | null, attachments: File[]) => Promise<any>,
    onReject?: (message: string | null, attachments: File[]) => Promise<any>,
    acceptButtonLabel?: string,
    rejectButtonLabel?: string,
    acceptButtonColor?: BootstrapColors,
    rejectButtonColor?: BootstrapColors,
}) {
    const userContext = useContext(UserContext);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [buttonsDisableState, setButtonsDisableState] = useState<Partial<Record<'accept' | 'reject', boolean>>>({});

    const [onDrop, onDelete] = useMemo(() => {
        return [
            makeOnDropAttachListener(setAttachments),
            makeOnDeleteAttachListener(setAttachments),
        ];
    }, [setAttachments]);

    const shouldRenderAcceptButton = props.hideAccept !== true && props.onAccept && (
            !props.acceptPermissions?.length ||
            userContext && userContext?.canAny(props.acceptPermissions)
    );
    const shouldRenderRejectButton = props.hideReject !== true && props.onReject && (
            !props.rejectPermissions?.length ||
            userContext && userContext?.canAny(props.rejectPermissions)
    );

    if (!shouldRenderAcceptButton && !shouldRenderRejectButton) {
        return null;
    }

    return <>
        {props.withoutCommentField ? null : (
                <MessageReceiver
                        defaultValue={message}
                        onInput={setMessage}
                        onDrop={onDrop}
                        placeholder={props.commentFieldPlaceholder ?? 'OrderAttentionsField.enter-comment'}
                        filesPreviewSlot={
                            <AttachmentsPreviews attachment={attachments} onDelete={onDelete}/>
                        }
                />
        )}

        <div className="row mt-2">
            {!shouldRenderAcceptButton ? null : (
                    <div className={classnames({
                        'col-6 text-start': shouldRenderRejectButton,
                        'col text-end': !shouldRenderRejectButton,
                    })}>
                        <Translate<HTMLButtonElement>
                                message={props.acceptButtonLabel ?? 'OrderAttentionsField.controls.accept'}
                                tag="button"
                                type="button"
                                disabled={buttonsDisableState.accept || !props.onAccept}
                                className={classnames([
                                    'btn',
                                    `btn-${props.acceptButtonColor ?? 'danger'}`
                                ])}
                                onClick={() => {
                                    if (props.onAccept) {
                                        setButtonsDisableState({accept: true});
                                        props.onAccept(message, attachments).finally(() => {
                                            setButtonsDisableState({accept: false});
                                        });
                                    }
                                }}
                        />
                    </div>
            )}
            {!shouldRenderRejectButton ? null : (
                    <div className={classnames('text-end', {
                        'col-6': shouldRenderAcceptButton,
                        'col': !shouldRenderAcceptButton,
                    })}>
                        <Translate<HTMLButtonElement>
                                message={props.rejectButtonLabel ?? 'OrderAttentionsField.controls.reject'}
                                tag="button"
                                type="button"
                                disabled={buttonsDisableState.reject || !props.onReject}
                                className={classnames([
                                    'btn',
                                    `btn-${props.rejectButtonColor ?? 'secondary'}`
                                ])}
                                onClick={() => {
                                    if (props.onReject) {
                                        setButtonsDisableState({reject: true});
                                        props.onReject(message, attachments).finally(() => {
                                            setButtonsDisableState({reject: false});
                                        });
                                    }
                                }}
                        />
                    </div>
            )}
        </div>
    </>;
}
